import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useVocabulary } from './contexts/VocabularyContext';
import { VocabularyTabs, StudentSelector } from './components/VocabularyTabs';
import QuizTitleEditor from './components/QuizTitleEditor';
import StudyDatePicker from './components/StudyDatePicker';
import useSpeech from './hooks/useSpeech';
import './css/Vocabulary.css';
import './css/VocabularyQuiz.css';

// A4 실측 (96dpi 기준). CSS의 .book-page 와 반드시 같아야 한다
const A4_W = 794;
const A4_H = 1123;
const PAD_X = 52;
const PAD_Y = 48;
const COL_GAP = 28;
const FOOTER_H = 36;
const CONTENT_W = A4_W - PAD_X * 2;                    // 690
const COL_W = Math.floor((CONTENT_W - COL_GAP) / 2);   // 331
const CONTENT_H = A4_H - PAD_Y * 2 - FOOTER_H;         // 1005
const CAPTURE_SCALE = 2;

/**
 * 실측한 높이로 단어를 2단 페이지에 채운다.
 *
 * 머리글은 페이지 전체 폭을 차지하므로 그 아래 두 열이 모두 짧아진다.
 * 한쪽 열에서만 빼면 반대쪽이 페이지 밖으로 흘러 페이지 번호를 덮는다.
 */
const paginate = (items, heights, firstPageOffset) => {
  const pages = [];
  let idx = 0;
  let isFirstPage = true;

  while (idx < items.length) {
    const limit = CONTENT_H - (isFirstPage ? firstPageOffset : 0);
    const cols = [[], []];

    for (let c = 0; c < 2 && idx < items.length; c++) {
      let used = 0;
      while (idx < items.length) {
        const h = heights.get(items[idx].key) || 0;
        // used > 0 조건이 있어야 한 항목이 한 열보다 길어도 무한 루프가 되지 않는다
        if (used > 0 && used + h > limit) break;
        cols[c].push(items[idx]);
        used += h;
        idx++;
      }
    }

    pages.push(cols);
    isFirstPage = false;
  }
  return pages;
};

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const formatCreated = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${formatDate(value)} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * 날짜에 붙인 이름으로 요약한다.
 * 같은 이름이 여러 날짜에 걸쳐 있으면 한 번만 쓰고, 많으면 앞뒤만 보여준다.
 */
const summarizeNames = (book) => {
  const names = book.aliases && book.aliases.length > 0 ? book.aliases : book.dates;
  if (!names || names.length === 0) return '';
  const unique = [...new Set(names)];
  if (unique.length <= 3) return unique.join(', ');
  return `${unique[0]} ~ ${unique[unique.length - 1]} (${unique.length})`;
};

/**
 * 단어장 — 고른 날짜의 단어를 모아 한 권씩 저장하고 A4 2단으로 뽑는다.
 *
 * 시험처럼 만들 때마다 저장되므로 이전에 만든 단어장이 사라지지 않는다.
 * 목록에서 고르면 같은 화면에 그대로 펼쳐진다.
 */
const VocabularyWordbook = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { wordbookId } = useParams();
  const { dates, fetchDates, wordbookApi, studentId } = useVocabulary();
  const { supported: canSpeak, speak, stop, speaking } = useSpeech();

  const [selectedDates, setSelectedDates] = useState([]);
  const [onlyImportant, setOnlyImportant] = useState(false);
  const [title, setTitle] = useState('');
  const [books, setBooks] = useState([]);
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pages, setPages] = useState(null);
  const [scale, setScale] = useState(1);

  const measureRef = useRef(null);
  const pageRefs = useRef([]);
  const previewRef = useRef(null);

  const targetUserId = studentId || undefined;

  /*
   * wordbookApi 는 매 렌더 새로 만들어지는 객체다. 의존성에 넣으면
   * 목록을 받아 → 다시 렌더 → 또 받는 무한 요청이 된다.
   */
  const loadBooks = useCallback(async () => {
    const data = await wordbookApi.listWordbooks({ limit: 50 }, targetUserId);
    if (data.success) setBooks(data.wordbooks || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId]);

  useEffect(() => { fetchDates(targetUserId); }, [fetchDates, targetUserId]);
  useEffect(() => { loadBooks(); }, [loadBooks]);
  // 관찰 대상이 바뀌면 고른 날짜도 펼쳐둔 단어장도 남의 것이라 의미가 없다
  useEffect(() => { setSelectedDates([]); setBook(null); setPages(null); }, [targetUserId]);
  // 화면을 떠날 때 읽던 발음을 끊는다
  useEffect(() => () => stop(), [stop]);

  // 주소로 들어오거나 목록에서 고르면 그 단어장을 펼친다
  useEffect(() => {
    if (!wordbookId) return undefined;
    let alive = true;
    setLoading(true);
    setPages(null);
    wordbookApi.getWordbook(wordbookId, targetUserId).then(data => {
      if (!alive) return;
      if (!data.success) {
        toast.error(t('vocabulary.wordbook.notFound'));
        navigate('/vocabulary/wordbook');
        return;
      }
      setBook(data);
      setLoading(false);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordbookId, targetUserId]);

  const handleCreate = async () => {
    if (selectedDates.length === 0) {
      toast.error(t('vocabulary.quiz.selectDates'));
      return;
    }
    setLoading(true);
    setPages(null);
    try {
      const data = await wordbookApi.createWordbook({
        title: title.trim(),
        dates: [...selectedDates].sort(),
        only_important: onlyImportant,
      }, targetUserId);

      if (!data.success) {
        toast.error(t('vocabulary.wordbook.buildFailed'));
        return;
      }
      // 발음기호를 못 만든 단어가 있으면 조용히 넘기지 않는다
      if (data.phonetic_missing > 0) {
        toast.info(t('vocabulary.wordbook.phoneticMissing', { count: data.phonetic_missing }));
      }
      setTitle('');
      await loadBooks();
      // 주소를 바꾸면 위 effect 가 다시 받아오므로 여기서 바로 세워둔다
      setBook(data);
      navigate(`/vocabulary/wordbook/${data.wordbook_id}`, { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleRename = async (id, next) => {
    const data = await wordbookApi.updateWordbook(id, { title: next }, targetUserId);
    if (!data.success) {
      toast.error(t('error.save'));
      return false;
    }
    setBooks(prev => prev.map(b => (b.wordbook_id === id ? { ...b, title: data.wordbook.title } : b)));
    setBook(prev => (prev && prev.wordbook_id === id ? { ...prev, title: data.wordbook.title } : prev));
    return true;
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('vocabulary.wordbook.deleteConfirm'))) return;
    const data = await wordbookApi.deleteWordbook(id, targetUserId);
    if (!data.success) {
      toast.error(t('error.save'));
      return;
    }
    toast.success(t('deleted'));
    setBooks(prev => prev.filter(b => b.wordbook_id !== id));
    if (book && book.wordbook_id === id) {
      setBook(null);
      setPages(null);
      navigate('/vocabulary/wordbook', { replace: true });
    }
  };

  const items = useMemo(
    () => (book?.words || []).map((w, i) => ({ key: `w${w.word_id}-${i}`, w })),
    [book]
  );

  // 열 폭에서 실측한 높이로 페이지를 만든다
  useEffect(() => {
    if (!book || pages !== null) return;
    const node = measureRef.current;
    if (!node) return;

    /*
     * 바깥 여백까지 더해서 잰다. 항목의 margin 은 래퍼 밖으로 collapse 되어
     * getBoundingClientRect().height 에 잡히지 않는다 — 그대로 쓰면 열마다
     * 여백 합계만큼 적게 보고 페이지 번호 위로 넘쳐 흐른다.
     */
    const heights = new Map();
    node.querySelectorAll('[data-mkey]').forEach(el => {
      const child = el.firstElementChild;
      let margin = 0;
      if (child) {
        const style = window.getComputedStyle(child);
        margin = (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
      }
      heights.set(el.dataset.mkey, el.getBoundingClientRect().height + margin);
    });
    const headH = node.querySelector('[data-mhead]')?.getBoundingClientRect().height || 0;

    pageRefs.current = [];
    setPages(paginate(items, heights, headH).map((cols, i) => ({ cols, header: i === 0 })));
  }, [book, pages, items]);

  /*
   * A4는 794px 고정이라 태블릿 폭에 그대로 두면 잘린다. 담을 수 있는 만큼 줄여 보여준다.
   * 캡처는 원본 크기로 해야 하므로 PDF를 뜰 때는 축소를 잠시 푼다.
   */
  const fitScale = useCallback(() => {
    const node = previewRef.current;
    if (!node) return;
    const usable = node.clientWidth - 32;
    setScale(usable > 0 ? Math.min(1, usable / A4_W) : 1);
  }, []);

  useEffect(() => {
    if (!pages) return undefined;
    fitScale();
    window.addEventListener('resize', fitScale);
    return () => window.removeEventListener('resize', fitScale);
  }, [pages, fitScale]);

  const handleExport = async () => {
    if (!pages || pages.length === 0 || exporting) return;
    setExporting(true);
    // 축소를 풀고 발음 버튼을 뺀 화면이 그려진 뒤에 캡처해야 한다
    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < pages.length; i++) {
        const node = pageRefs.current[i];
        if (!node) continue;
        // 페이지 단위로 캡처한다 (전체를 한 번에 뜨면 캔버스 높이 한계에 걸린다)
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(node, {
          scale: CAPTURE_SCALE,
          backgroundColor: '#ffffff',
          useCORS: true,
          logging: false,
          width: A4_W,
          height: A4_H,
          windowWidth: A4_W,
        });
        if (i > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pw, ph);
      }

      const range = book.dates.length === 0
        ? String(book.wordbook_id)
        : (book.dates.length === 1
          ? book.dates[0]
          : `${book.dates[0]}~${book.dates[book.dates.length - 1]}`);
      const name = book.title || t('vocabulary.wordbook.title');
      pdf.save(`${name}_${range}.pdf`.replace(/[^\w가-힣.~-]+/g, '_'));
    } catch (e) {
      console.error('Wordbook PDF export failed:', e);
      toast.error(t('vocabulary.quiz.pdfFailed'));
    } finally {
      setExporting(false);
    }
  };

  const renderHeader = () => (
    <header className="book-head">
      <h1 className="book-title">{book.title || t('vocabulary.wordbook.title')}</h1>
      <p className="book-info">
        {t('vocabulary.wordbook.meta', { count: book.total })}
        {book.dates.length > 0 && ` · ${summarizeNames(book)}`}
      </p>
    </header>
  );

  // 종이에서는 발음 버튼이 의미가 없어 화면용과 인쇄용을 나눈다
  const renderWord = (w, withSpeak) => (
    <div className="book-word">
      <div className="book-word-main">
        <strong className="book-term">{w.word}</strong>
        {w.part_of_speech && <em className="book-pos">{w.part_of_speech}</em>}
        {w.phonetic && <span className="book-ipa">[{w.phonetic}]</span>}
        {w.is_important && <span className="book-star" aria-hidden="true">★</span>}
        {withSpeak && canSpeak && (
          <button
            className={`book-speak${speaking === `s${w.word_id}` ? ' on' : ''}`}
            onClick={() => speak(w.word, `s${w.word_id}`)}
            title={t('vocabulary.wordbook.listen')}
            aria-label={`${w.word} ${t('vocabulary.wordbook.listen')}`}
          >
            &#128266;
          </button>
        )}
      </div>
      <div className="book-meaning">{w.meaning}</div>
      {w.note && <div className="book-note">{w.note}</div>}
    </div>
  );

  return (
    <div className="vocab-container">
      <div className="vocab-header">
        <h2>{t('vocabulary.wordbook.title')}</h2>
        <div className="vocab-header-actions">
          <StudentSelector />
        </div>
      </div>

      <VocabularyTabs active="wordbook" />

      <div className="quiz-setup">
        <h3>{t('vocabulary.wordbook.newBook')}</h3>

        <div className="quiz-field">
          <label htmlFor="wordbook-title">{t('vocabulary.wordbook.name')}</label>
          <input
            id="wordbook-title"
            type="text"
            maxLength={100}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('vocabulary.wordbook.namePlaceholder')}
          />
        </div>

        <StudyDatePicker
          dates={dates}
          selected={selectedDates}
          onChange={setSelectedDates}
        />

        <div className="quiz-toggles">
          <label className="vocab-checkbox">
            <input
              type="checkbox"
              checked={onlyImportant}
              onChange={(e) => setOnlyImportant(e.target.checked)}
            />
            {t('vocabulary.onlyImportant')}
          </label>
        </div>

        <button
          className="vocab-btn vocab-btn-primary quiz-start-btn"
          onClick={handleCreate}
          disabled={loading}
        >
          {loading ? t('vocabulary.wordbook.building') : t('vocabulary.wordbook.build')}
        </button>
        <p className="quiz-hint">{t('vocabulary.wordbook.buildHint')}</p>
      </div>

      {/* 만든 단어장은 사라지지 않고 여기 쌓인다 */}
      <div className="quiz-history">
        <h3>{t('vocabulary.wordbook.savedBooks')}</h3>
        {books.length === 0 && <p className="vocab-empty">{t('vocabulary.wordbook.noBooks')}</p>}
        <ul className="quiz-list">
          {books.map(b => (
            <li
              key={b.wordbook_id}
              className={`quiz-item${book && book.wordbook_id === b.wordbook_id ? ' open' : ''}`}
            >
              <div className="quiz-item-main">
                <QuizTitleEditor
                  title={b.title}
                  onSave={(next) => handleRename(b.wordbook_id, next)}
                  className="quiz-item-title"
                  untitledKey="vocabulary.wordbook.untitled"
                  renameKey="vocabulary.wordbook.rename"
                  placeholderKey="vocabulary.wordbook.namePlaceholder"
                />
                <span className="quiz-item-meta">
                  {t('vocabulary.wordbook.meta', { count: b.word_count })}
                  {b.only_important && ` · ${t('vocabulary.onlyImportant')}`}
                  {b.dates.length > 0 && ` · ${summarizeNames(b)}`}
                  {` · ${formatCreated(b.created_at)}`}
                </span>
              </div>
              <div className="quiz-item-actions">
                <button
                  className="vocab-btn vocab-btn-primary"
                  onClick={() => navigate(`/vocabulary/wordbook/${b.wordbook_id}`)}
                >
                  {t('vocabulary.wordbook.open')}
                </button>
                <button className="vocab-btn vocab-btn-danger" onClick={() => handleDelete(b.wordbook_id)}>
                  {t('delete')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {book && (
        <>
          <div className="paper-toolbar">
            <span className="paper-pagecount">
              {t('vocabulary.wordbook.meta', { count: book.total })}
              {pages && ` · ${t('vocabulary.quiz.paperPages', { count: pages.length })}`}
            </span>
            <button
              className="vocab-btn vocab-btn-primary"
              onClick={handleExport}
              disabled={exporting || !pages}
            >
              {exporting ? t('vocabulary.loading') : t('vocabulary.quiz.downloadPdf')}
            </button>
            {canSpeak && <span className="quiz-hint">{t('vocabulary.wordbook.listenHint')}</span>}
          </div>

          {/* 1단계: 열 폭에서 높이를 실측한다 (화면 밖) */}
          {pages === null && (
            <div className="quiz-paper-offscreen" ref={measureRef} aria-hidden="true">
              <div style={{ width: CONTENT_W }} data-mhead>{renderHeader()}</div>
              <div style={{ width: COL_W }}>
                {items.map(it => (
                  <div key={it.key} data-mkey={it.key}>{renderWord(it.w, false)}</div>
                ))}
              </div>
            </div>
          )}

          {/* 2단계: 페이지별로 조판 */}
          <div className="paper-preview" ref={previewRef}>
            {pages === null && <p className="vocab-empty">{t('vocabulary.loading')}</p>}
            {pages && pages.map((page, i) => (
              <div
                className="book-scaler"
                key={i}
                // 캡처할 때는 축소를 풀어야 원본 해상도로 뜬다
                style={{ '--book-scale': exporting ? 1 : scale }}
              >
                <div className="book-page" ref={(el) => { pageRefs.current[i] = el; }}>
                  {page.header && renderHeader()}
                  <div className="book-cols">
                    {page.cols.map((colItems, c) => (
                      <div className="book-col" key={c}>
                        {colItems.map(it => (
                          // 종이에서는 누를 수 없으니 캡처 중에는 발음 버튼을 뺀다
                          <React.Fragment key={it.key}>{renderWord(it.w, !exporting)}</React.Fragment>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="book-footer">
                    {i + 1} / {pages.length}
                    <span className="book-footer-date">{formatDate(book.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default VocabularyWordbook;
